/// <reference types="@angular/localize" />

import {
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
} from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router, RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';
import * as Sentry from '@sentry/angular';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { AUTH, FIRESTORE, FUNCTIONS, STORAGE } from './app/firebase-injection';
import { environment } from './environments/environment';

const themeV1 = {
  foreground: '#000000',
  background: '#ffffff',
  accentBackground: '#313757',
  alternativeBackground: '#878cc4',
  accentForeground: '#ffffff',
};
// Prepared for theme v2 as per ticket #406. Do not change data structure here as all values are referenced somewhere.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const themeV2 = {
  foreground: '#2E6C93',
  background: '#ffffff',
  accentBackground: '#206890',
  alternativeBackground: '#4490c4',
  accentForeground: '#ffffff',
};
export const activeTheme = themeV1;

Sentry.init({
  dsn: 'https://4ae8bfae02b92f3b8942a24db855fc83@o4508635322056704.ingest.de.sentry.io/4509269802025040',
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      autoInject: false,
      themeLight: {
        foreground: activeTheme.foreground,
        background: activeTheme.background,
        accentBackground: activeTheme.accentBackground,
        accentForeground: activeTheme.accentForeground,
      },
      themeDark: {
        foreground: activeTheme.foreground,
        background: activeTheme.background,
        accentBackground: activeTheme.accentBackground,
        accentForeground: activeTheme.accentForeground,
      },
      showBranding: false,
      formTitle: $localize`:@@sentryFormTitle:Share Feedback`,
      submitButtonLabel: $localize`:@@sentrySubmitButtonLabel:Share Feedback`,
      cancelButtonLabel: $localize`:@@Main_Cancel:Cancel`,
      confirmButtonLabel: $localize`:@@Main_Confirm:Confirm`, // in allowlist
      addScreenshotButtonLabel: $localize`:@@sentryAddScreenshotButtonLabel:Add Screenshot`,
      removeScreenshotButtonLabel: $localize`:@@sentryRemoveScreenshotButtonLabel:Remove Screenshot`,
      nameLabel: $localize`:@@Main_Name:Name`, // in allowlist
      namePlaceholder: $localize`:@@sentryNamePlaceholder:Your name`,
      emailLabel: $localize`:@@EmailLabel:Email`,
      isRequiredLabel: $localize`:@@sentryIsRequiredLabel:(required)`,
      messageLabel: $localize`:@@Description:Description`,
      messagePlaceholder: $localize`:@@sentryMessagePlaceholder:What happened? What did you expect?`,
      emailPlaceholder: $localize`:@@sentryEmailPlaceholder:max.mustermann@gmail.com`,
      successMessageText: $localize`:@@sentrySuccessMessageText:Thank you for your feedback!`,
    }),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost'],
  // Session Replay
  // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysSessionSampleRate: isDevMode() ? 1 : 0.1,
  // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  replaysOnErrorSampleRate: 1.0,
});

const firebaseApp = initializeApp(environment.firebase);

const auth = getAuth(firebaseApp);
if (environment.useFirebaseEmulators) {
  connectAuthEmulator(auth, 'http://localhost:9099', {
    disableWarnings: true,
  });
}

const firestore = initializeFirestore(firebaseApp, {
  localCache: persistentLocalCache(),
});
if (environment.useFirebaseEmulators) {
  connectFirestoreEmulator(firestore, 'localhost', 8080);
}

const functions = getFunctions(firebaseApp, 'europe-west3');
if (environment.useFirebaseEmulators) {
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

const storage = getStorage(firebaseApp);
if (environment.useFirebaseEmulators) {
  connectStorageEmulator(storage, 'localhost', 9199);
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(
      BrowserModule,
      RouterModule,
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: !isDevMode(),
        registrationStrategy: 'registerWhenStable:30000',
      }),
      NgCircleProgressModule.forRoot({
        radius: 100,
        outerStrokeWidth: 16,
        innerStrokeWidth: 8,
        showBackground: false,
        startFromZero: false,
      })
    ),
    { provide: AUTH, useValue: auth },
    { provide: FIRESTORE, useValue: firestore },
    { provide: FUNCTIONS, useValue: functions },
    { provide: STORAGE, useValue: storage },
    provideRouter(routes),
    provideAnimations(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      inject(Sentry.TraceService);
    }),
    ...environment.providers,
  ],
}).catch((err) => console.error(err));
